import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Stack, Alert, CircularProgress, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, MenuItem, Select, FormControl, InputLabel, Card, CardContent, Grid2 as Grid,
} from '@mui/material';
import { Refresh, AutoAwesome, CheckCircle, Receipt, TrendingUp, People } from '@mui/icons-material';
import api from '../../../services/api';
import { nowCv } from '../../../utils/date';

interface FaturaComissao {
  ID: string;
  NUMERO: string;
  agenteId: string;
  agenteNome: string;
  agenteCodigo: string;
  PERIODO_MES: number;
  PERIODO_ANO: number;
  TOTAL_TRANSACOES: number;
  TOTAL_VOLUME: number;
  TOTAL_COMISSAO: number;
  TOTAL_IVA: number;
  TOTAL_PAGAR: number;
  ESTADO: 'RASCUNHO' | 'EMITIDA' | 'PAGA' | 'CANCELADA';
  DT_EMISSAO: string;
  DT_VENCIMENTO: string;
  DT_PAGAMENTO: string | null;
}

const ESTADO_COLOR: Record<string, any> = {
  RASCUNHO: 'default', EMITIDA: 'info', PAGA: 'success', CANCELADA: 'error',
};

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const now = nowCv(); // mês/ano de CV (ler com getUTC*)

export default function Comissoes() {
  const [faturas, setFaturas] = useState<FaturaComissao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [gerarDialog, setGerarDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pagarDialog, setPagarDialog] = useState<FaturaComissao | null>(null);
  const [pagando, setPagando] = useState(false);

  const prevMonth = now.getUTCMonth() === 0 ? 12 : now.getUTCMonth();
  const prevYear = now.getUTCMonth() === 0 ? now.getUTCFullYear() - 1 : now.getUTCFullYear();
  const [gerarForm, setGerarForm] = useState({ mes: prevMonth, ano: prevYear });

  const fetchFaturas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/agentes/faturacao');
      setFaturas(res.data?.data || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar facturas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFaturas(); }, [fetchFaturas]);

  const handleGerar = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/admin/agentes/faturacao/gerar', gerarForm);
      const d = res.data?.data;
      setGerarDialog(false);
      setSuccess(
        `Geradas ${d?.geradas ?? d?.faturasGeradas ?? 0} facturas` +
        (d?.jaExistiam ? ` (${d.jaExistiam} já existiam)` : '') +
        ` para ${MESES[gerarForm.mes - 1]} ${gerarForm.ano}.`
      );
      fetchFaturas();
    } catch (err: any) {
      setError(err.message || 'Erro ao gerar facturas');
    } finally {
      setGenerating(false);
    }
  };

  const handlePagar = async () => {
    if (!pagarDialog) return;
    setPagando(true);
    try {
      await api.put(`/admin/agentes/faturacao/${pagarDialog.ID}/pagar`);
      setPagarDialog(null);
      setSuccess(`Factura ${pagarDialog.NUMERO} marcada como paga.`);
      fetchFaturas();
    } catch (err: any) {
      setError(err.message || 'Erro ao marcar como paga');
    } finally {
      setPagando(false);
    }
  };

  const fmt = (n: number) =>
    new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(n);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('pt-PT', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' });

  // Totais
  const totalComissoes = faturas.reduce((s, f) => s + (f.TOTAL_COMISSAO || 0), 0);
  const totalPagar = faturas.reduce((s, f) => s + (f.ESTADO !== 'PAGA' && f.ESTADO !== 'CANCELADA' ? (f.TOTAL_PAGAR || 0) : 0), 0);
  const totalPagas = faturas.filter(f => f.ESTADO === 'PAGA').length;
  const agentesDistintos = new Set(faturas.map(f => f.agenteId)).size;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>Comissões de Agentes</Typography>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={fetchFaturas} disabled={loading}><Refresh /></IconButton>
          <Button variant="contained" startIcon={<AutoAwesome />} onClick={() => setGerarDialog(true)}>
            Gerar Facturas
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      {/* Stats */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Total Comissões', value: fmt(totalComissoes), icon: <TrendingUp color="success" />, color: 'success.light' },
          { label: 'A Pagar', value: fmt(totalPagar), icon: <Receipt color="warning" />, color: 'warning.light' },
          { label: 'Facturas Pagas', value: `${totalPagas} / ${faturas.length}`, icon: <CheckCircle color="info" />, color: 'info.light' },
          { label: 'Agentes', value: agentesDistintos, icon: <People color="primary" />, color: 'primary.light' },
        ].map(s => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={s.label}>
            <Card variant="outlined">
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ bgcolor: s.color, borderRadius: 2, p: 1, display: 'flex' }}>{s.icon}</Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                  <Typography variant="h6" fontWeight={600}>{s.value}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabela */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><strong>Número</strong></TableCell>
                <TableCell><strong>Agente</strong></TableCell>
                <TableCell><strong>Período</strong></TableCell>
                <TableCell align="right"><strong>Transacções</strong></TableCell>
                <TableCell align="right"><strong>Volume</strong></TableCell>
                <TableCell align="right"><strong>Comissão</strong></TableCell>
                <TableCell align="right"><strong>IVA (15%)</strong></TableCell>
                <TableCell align="right"><strong>Total</strong></TableCell>
                <TableCell><strong>Estado</strong></TableCell>
                <TableCell><strong>Vencimento</strong></TableCell>
                <TableCell align="center"><strong>Acções</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {faturas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhuma factura. Clique em "Gerar Facturas" para gerar as comissões do mês.
                  </TableCell>
                </TableRow>
              ) : faturas.map(f => (
                <TableRow key={f.ID} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{f.NUMERO}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    <Typography variant="body2" fontSize={12} fontWeight={500}>{f.agenteNome}</Typography>
                    <Typography variant="caption" color="text.secondary">{f.agenteCodigo}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{MESES[f.PERIODO_MES - 1].slice(0, 3)} {f.PERIODO_ANO}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{f.TOTAL_TRANSACOES}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(f.TOTAL_VOLUME)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: 'success.main', fontWeight: 600 }}>
                    {fmt(f.TOTAL_COMISSAO)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(f.TOTAL_IVA)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700 }}>{fmt(f.TOTAL_PAGAR)}</TableCell>
                  <TableCell>
                    <Chip label={f.ESTADO} size="small" color={ESTADO_COLOR[f.ESTADO]} sx={{ fontSize: 11 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{f.DT_VENCIMENTO ? formatDate(f.DT_VENCIMENTO) : '—'}</TableCell>
                  <TableCell align="center">
                    {f.ESTADO === 'EMITIDA' && (
                      <Tooltip title="Marcar como paga">
                        <Button size="small" variant="outlined" color="success"
                          onClick={() => setPagarDialog(f)} sx={{ fontSize: 11 }}>
                          Pagar
                        </Button>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog: Gerar Facturas */}
      <Dialog open={gerarDialog} onClose={() => setGerarDialog(false)}>
        <DialogTitle>Gerar Facturas de Comissão</DialogTitle>
        <DialogContent sx={{ minWidth: 320 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Gera facturas de comissão para todos os agentes activos com transacções no período seleccionado.
            A operação é idempotente — não duplica facturas já existentes.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={7}>
              <FormControl fullWidth size="small">
                <InputLabel>Mês</InputLabel>
                <Select value={gerarForm.mes} label="Mês"
                  onChange={e => setGerarForm(f => ({ ...f, mes: Number(e.target.value) }))}>
                  {MESES.map((m, i) => (
                    <MenuItem key={i} value={i + 1}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={5}>
              <TextField fullWidth size="small" label="Ano" type="number"
                value={gerarForm.ano}
                onChange={e => setGerarForm(f => ({ ...f, ano: Number(e.target.value) }))}
                inputProps={{ min: 2024, max: 2030 }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGerarDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGerar} disabled={generating} startIcon={<AutoAwesome />}>
            {generating ? <CircularProgress size={18} /> : 'Gerar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmar Pagamento */}
      <Dialog open={!!pagarDialog} onClose={() => setPagarDialog(null)}>
        <DialogTitle>Confirmar Pagamento</DialogTitle>
        <DialogContent>
          <Typography>
            Confirma que a factura <strong>{pagarDialog?.NUMERO}</strong> foi paga?
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Agente: {pagarDialog?.agenteNome} — Total: {pagarDialog ? fmt(pagarDialog.TOTAL_PAGAR) : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPagarDialog(null)}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handlePagar} disabled={pagando}>
            {pagando ? <CircularProgress size={18} /> : 'Confirmar Pagamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
